import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import {
  BookingRowsView,
  DiscountControls,
  FareSummaryView,
  JsonPanel,
  UserDropdown,
  show,
  useNotificationPolling,
  userLabel,
} from "./createBookingShared";

// Separate-PNR round trip: onward and return were booked on TBO as two PNRs.
// Saved as two bookings rows (trip_indicator_id 1 and 2) sharing one trace_id.
// Each leg is looked up by PNR + BookingId, or PNR + passenger first/last name.
const EMPTY_LEG = { pnr: "", tboBookingId: "", firstName: "", lastName: "" };

function LegInputs({ title, value, onChange }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-full text-sm font-semibold text-gray-700">{title}</div>
      <div className="w-full sm:w-auto sm:min-w-[200px]">
        <label className="text-sm font-semibold">PNR</label>
        <input
          className="mt-2 w-full border rounded-xl px-3 py-2 uppercase outline-none focus:ring-2 focus:ring-black"
          placeholder="e.g. L2ULGX"
          value={value.pnr}
          onChange={(e) => onChange({ ...value, pnr: e.target.value })}
        />
      </div>
      <div className="w-full sm:w-auto sm:min-w-[200px]">
        <label className="text-sm font-semibold">Booking ID</label>
        <input
          className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
          placeholder="TBO BookingId"
          inputMode="numeric"
          value={value.tboBookingId}
          onChange={(e) => onChange({ ...value, tboBookingId: e.target.value.replace(/\D/g, "") })}
        />
      </div>
      <div className="w-full sm:w-auto sm:min-w-[160px]">
        <label className="text-sm font-semibold">First Name</label>
        <input
          className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
          placeholder="If no Booking ID"
          value={value.firstName}
          onChange={(e) => onChange({ ...value, firstName: e.target.value })}
        />
      </div>
      <div className="w-full sm:w-auto sm:min-w-[160px]">
        <label className="text-sm font-semibold">Last Name</label>
        <input
          className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
          placeholder="If no Booking ID"
          value={value.lastName}
          onChange={(e) => onChange({ ...value, lastName: e.target.value })}
        />
      </div>
    </div>
  );
}

function LegWarnings({ leg }) {
  const t = leg.ticketing;
  return (
    <>
      {t && t.ticketed < t.total && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {leg.label} leg not fully ticketed: {t.ticketed}/{t.total} passengers have a ticket number.
        </div>
      )}
      {leg.existing_booking && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {leg.label} leg already exists (booking #{leg.existing_booking.booking_id},{" "}
          {leg.existing_booking.booking_ticket_number || "no ticket ref"}).{leg.warning ? ` ${leg.warning}` : ""}
        </div>
      )}
      {leg.convert_error && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {leg.label} leg conversion failed: {leg.convert_error}
        </div>
      )}
    </>
  );
}

function NotificationLine({ label, n }) {
  if (!n) return null;
  return (
    <div className="text-gray-700">
      {label}: Push <b>{show(n.push)}</b> · Ticket email <b>{show(n.ticket_email)}</b> · Invoice <b>{show(n.invoice)}</b>
    </div>
  );
}

export default function CreateRoundTripFromTbo() {
  const navigate = useNavigate();
  const [onward, setOnward] = useState(EMPTY_LEG);
  const [ret, setRet] = useState(EMPTY_LEG);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [allowUnticketed, setAllowUnticketed] = useState(false);
  // The convenience fee is always applied; there is no toggle.
  const showFee = true;
  const [paymentId, setPaymentId] = useState("");
  // Fare Summary choices; the server recalculates the onward leg with them.
  const [promoCode, setPromoCode] = useState("");
  const [redeem, setRedeem] = useState("");
  const [redeemAmount, setRedeemAmount] = useState("");
  const [recalculating, setRecalculating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  useNotificationPolling(created?.notifications_booking_id, created?.notifications?.status, ({ status, notifications }) =>
    setCreated((c) => (c ? { ...c, notifications: { status, ...(notifications || {}) } } : c))
  );

  const legFilled = (l) => l.pnr.trim() && (l.tboBookingId.trim() || (l.firstName.trim() && l.lastName.trim()));
  const canSubmit = legFilled(onward) && legFilled(ret) && user && !loading;

  const legBody = (l) => ({
    pnr: l.pnr.trim(),
    tbo_booking_id: l.tboBookingId.trim() || undefined,
    first_name: l.firstName.trim() || undefined,
    last_name: l.lastName.trim() || undefined,
  });
  const legsBody = () => [legBody(onward), legBody(ret)];

  const fetchDetails = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setErr("");
    setResult(null);
    setCreated(null);
    setAllowUnticketed(false);
    setPaymentId("");
    setPromoCode("");
    setRedeem("");
    setRedeemAmount("");
    try {
      const res = await api.post("/admin/bookings/create-from-tbo/roundtrip/preview", {
        user_id: user.user_id,
        legs: legsBody(),
      });
      setResult(res.data?.data || null);
    } catch (e2) {
      const msg = e2?.response?.data?.message || e2.message || "Failed to fetch bookings from TBO";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Re-runs the preview with the chosen promo / redeem so the fee is
  // recalculated server-side exactly as confirm will save it.
  const recalculateFee = async (nextPromo, nextRedeem, nextAmount = "") => {
    setPromoCode(nextPromo);
    setRedeem(nextRedeem);
    setRedeemAmount(nextAmount);
    setRecalculating(true);
    try {
      const res = await api.post("/admin/bookings/create-from-tbo/roundtrip/preview", {
        user_id: user.user_id,
        legs: legsBody(),
        promo_code: nextPromo || undefined,
        redeem: nextRedeem || undefined,
        redeem_amount: nextRedeem && nextAmount ? Number(nextAmount) : undefined,
      });
      setResult(res.data?.data || null);
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Fee recalculation failed");
    } finally {
      setRecalculating(false);
    }
  };

  const legs = result?.legs || [];
  const feeApplied = showFee && !!result?.grand_total;
  const legRows = (leg) => (feeApplied && leg.converted_response_with_fee ? leg.converted_response_with_fee : leg.converted_response);
  const anyNotTicketed = legs.some((l) => l.ticketing && l.ticketing.ticketed < l.ticketing.total);
  const canCreate =
    legs.length === 2 &&
    legs.every((l) => legRows(l) && !l.existing_booking && l.ticketing?.total > 0) &&
    !created &&
    !creating &&
    !recalculating &&
    (!anyNotTicketed || allowUnticketed);

  const createBooking = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const res = await api.post("/admin/bookings/create-from-tbo/roundtrip/confirm", {
        user_id: user.user_id,
        legs: legsBody(),
        trace_id: legRows(legs[0])?.booking?.trace_id,
        allow_unticketed: anyNotTicketed ? allowUnticketed : undefined,
        apply_convenience_fee: feeApplied,
        promo_code: feeApplied ? promoCode || undefined : undefined,
        redeem: feeApplied ? redeem || undefined : undefined,
        redeem_amount: feeApplied && redeem && redeemAmount ? Number(redeemAmount) : undefined,
        payment_id: paymentId.trim() || undefined,
      });
      setCreated(res.data?.data || {});
      toast.success("Round trip created");
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Failed to create round trip");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <form onSubmit={fetchDetails} className="mt-6 border rounded-2xl p-4 space-y-4">
        <LegInputs title="Onward " value={onward} onChange={setOnward} />
        <LegInputs title="Return " value={ret} onChange={setRet} />
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto sm:min-w-[320px]">
            <label className="text-sm font-semibold">User</label>
            <UserDropdown value={user} onChange={setUser} />
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-xl bg-black text-white px-5 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? "Fetching..." : "Get Booking Details"}
          </button>
        </div>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      {result && (
        <div className="mt-6 space-y-4">
          <div className="border rounded-2xl p-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
            {legs.map((leg) => {
              const it = leg.actual_response?.Response?.FlightItinerary;
              return (
                <div key={leg.trip_indicator_id}>
                  <div className="text-gray-500">{leg.label} · PNR / Booking ID</div>
                  <div className="font-semibold">
                    {it?.PNR || leg.pnr} / {it?.BookingId || leg.tbo_booking_id} · {it?.Origin || "-"} → {it?.Destination || "-"}
                  </div>
                </div>
              );
            })}
            <div>
              <div className="text-gray-500">Trace ID (shared)</div>
              <div className="font-semibold break-all">{show(result.trace_id)}</div>
            </div>
            <div>
              <div className="text-gray-500">User</div>
              <div className="font-semibold truncate">{result.user ? userLabel(result.user) : "-"}</div>
            </div>
          </div>

          {legs.map((leg) => (
            <LegWarnings key={leg.trip_indicator_id} leg={leg} />
          ))}

          {legs.map((leg) => (
            <div key={leg.trip_indicator_id} className="space-y-4">
              <h2 className="text-lg font-bold pt-2">
                {leg.label} leg · trip_indicator_id {leg.trip_indicator_id}
              </h2>
              <BookingRowsView rows={legRows(leg)} paymentIdOverride={paymentId.trim()} />
            </div>
          ))}

          <div className="border rounded-2xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">
              Fare Summary · trip_indicator_id 1 (onward and return combined)
            </div>
            <div className="p-4 space-y-2">
              {result.fare_summary_error ? (
                <div className="text-sm text-red-600">Fee calculation failed: {result.fare_summary_error}</div>
              ) : !result.combined_fare_summary ? (
                <div className="text-sm text-gray-500">No fare summary available.</div>
              ) : (
                <div className="space-y-4">
                  <DiscountControls
                    result={result}
                    promoCode={promoCode}
                    redeem={redeem}
                    redeemAmount={redeemAmount}
                    disabled={!!created || recalculating}
                    onChange={recalculateFee}
                  />
                  {recalculating && <div className="text-sm text-gray-500">Recalculating fee...</div>}
                  {(result.discount_warnings || []).map((w) => (
                    <div key={w} className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                      {w}
                    </div>
                  ))}
                  <FareSummaryView summary={result.combined_fare_summary} />
                  <p className="text-xs text-gray-500">
                    Both legs combined for review. Each leg is saved with its own fare_info; the onward leg's totalfare is the
                    trip payable and the return leg's totalfare is 0. NavCash earn (onward leg only):{" "}
                    {show(result.navcash_earn)}.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {created ? (
              <div className="text-sm space-y-1">
                <div className="text-green-700">
                  Round trip created · trace_id {created.trace_id} · grand total {show(created.grand_total)} · NavCash earn{" "}
                  {show(created.navcash_earn)} (onward leg).
                </div>
                {(created.legs || []).map((l) => (
                  <div key={l.booking_id} className="text-gray-700">
                    {l.label}: <b>{l.booking_ticket_number}</b> · PNR {l.pnr} · total {show(l.totalfare)} · convenience fee{" "}
                    {show(l.convenience_fee)}
                  </div>
                ))}
                <div className="text-gray-700">
                  Payment ID: <b>{show(created.payment_id)}</b>
                  {created.payment_warning ? <span className="text-amber-700"> · {created.payment_warning}</span> : null}
                </div>
                {created.notifications?.status === "running" ? (
                  <div className="text-gray-600">Sending push notifications, ticket email and invoice...</div>
                ) : created.notifications?.status === "timeout" ? (
                  <div className="text-amber-700">Still sending in the background. Check the booking later.</div>
                ) : created.notifications ? (
                  <>
                    <NotificationLine label="Onward" n={created.notifications.onward} />
                    <NotificationLine label="Return" n={created.notifications.return} />
                    {created.notifications.error ? <div className="text-red-600">Error: {created.notifications.error}</div> : null}
                  </>
                ) : null}
              </div>
            ) : anyNotTicketed ? (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowUnticketed} onChange={(e) => setAllowUnticketed(e.target.checked)} />
                Create anyway, even though not every passenger is ticketed
              </label>
            ) : (
              <div className="text-sm text-gray-600">Review both legs above, then create the round trip.</div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              {!created && (
                <input
                  className="border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black sm:w-64"
                  placeholder="Payment ID (optional, pay_...)"
                  value={paymentId}
                  onChange={(e) => setPaymentId(e.target.value.trim())}
                />
              )}
              {created?.trace_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/bookings/edit/trace/${created.trace_id}`)}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                >
                  Open Booking
                </button>
              )}
              {!created && (
                <button
                  type="button"
                  onClick={createBooking}
                  disabled={!canCreate}
                  className="rounded-xl bg-black text-white px-5 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  {creating ? "Creating..." : "Create Booking"}
                </button>
              )}
            </div>
          </div>

          {legs.map((leg) => (
            <div key={leg.trip_indicator_id} className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
              <JsonPanel title={`${leg.label}: Actual Booking Response (TBO)`} data={leg.actual_response} />
              <JsonPanel
                title={`${leg.label}: Converted Booking Response${feeApplied ? " (with fee)" : ""}`}
                data={legRows(leg)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
